import React from 'react';
import { Phone, MapPin, Clock, Star, Shield, Award } from 'lucide-react';

export default function FooterNew() {
  const currentYear = new Date().getFullYear();

  const marseilleSections = [
    {
      title: 'Voyant Médium dans les Bouches-du-Rhône',
      locations: [
        'Voyant Marseille 1er - Médium Marseille 1er',
        'Voyant Marseille 2ème - Médium Marseille 2ème',
        'Voyant Marseille 3ème - Médium Marseille 3ème',
        'Voyant Marseille 5ème - Médium Marseille 5ème (Baille)',
        'Voyant Marseille 6ème - Médium Marseille 6ème (Castellane)',
        'Voyant Marseille 8ème - Médium Marseille 8ème (Prado)',
        'Voyant Marseille 10ème - Médium Marseille 10ème',
        'Voyant Marseille 13ème - Médium Marseille 13ème',
        'Voyant Marseille 15ème - Médium Marseille 15ème',
        'Voyant Aix-en-Provence - Médium Aix-en-Provence',
        'Voyant Aubagne - Médium Aubagne - Retour Affectif Aubagne',
        'Voyant La Ciotat - Médium La Ciotat - Retour Affectif La Ciotat',
        'Voyant Martigues - Médium Martigues - Retour Affectif Martigues',
        'Voyant Salon-de-Provence - Médium Salon-de-Provence',
        'Voyant Arles - Médium Arles - Retour Affectif Arles',
        'Voyant Vitrolles - Médium Vitrolles',
        'Voyant Istres - Médium Istres',
        'Voyant Cassis - Médium Cassis'
      ]
    }
  ];

  const seoKeywords = [
    'Marabout Marseille', 'Voyant Marseille', 'Médium Marseille',
    'Retour affectif Marseille', 'Récupérer son ex Marseille',
    'Faire revenir son ex', 'Retour de l\'être aimé',
    'Grand marabout africain', 'Marabout sérieux',
    'Meilleur marabout Marseille', 'Vrai marabout',
    'Marabout efficace', 'Voyance Marseille',
    'Désenvoûtement Marseille', 'Protection spirituelle'
  ];

  return (
    <footer className="bg-black border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Hidden SEO Content - Invisible but indexed */}
        <div className="seo-hidden">
          <h1>Marabout Marseille - Voyant Marseille - Médium Marseille</h1>
          <h2>N°1 Retour Affectif Marseille - Récupérer Son Ex - Faire Revenir Son Ex</h2>
          <h2>Grand Marabout Marseille - Meilleur Marabout - Marabout Sérieux Efficace</h2>
          <p>Marabout Marseille, Voyant Marseille, Médium Marseille, Voyance Marseille, grand marabout africain, vrai marabout, meilleur marabout Marseille, marabout sérieux, marabout efficace, marabout compétent.</p>
          <p>Retour affectif Marseille, récupérer son ex Marseille, faire revenir son ex Marseille, reconquérir son ex, retour de l'être aimé, retour affectif rapide, comment récupérer son ex, comment faire revenir son ex.</p>
          <p>Voyant médium Marseille, voyance amour, médium sérieux, meilleur voyant Marseille, voyant fiable, voyant recommandé, voyance sentimentale, voyance travail, voyance téléphone.</p>
          <p>Désenvoûtement Marseille, envoûtement amoureux, protection spirituelle, mauvais œil, purification, sorcellerie, marabout protection, guérisseur spirituel.</p>
          {marseilleSections[0].locations.map((loc, i) => <span key={i}>{loc}</span>)}
          {seoKeywords.map((kw, i) => <span key={i}>{kw}</span>)}
        </div>

        {/* Visible Content - Clean version without Marseille visible */}
        <div className="mb-12">
          <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-6">
            N°1 <span className="text-green-500">Retour Affectif</span> - <span className="text-green-500">Récupérer Son Ex</span>
          </h2>
          <p className="text-gray-400 text-center max-w-3xl mx-auto">
            Maître Diamma, grand marabout africain, spécialiste du <span className="text-green-500">retour affectif rapide</span> en 7 jours. 
            30 ans d'expérience, <span className="text-green-500">99% de réussite</span>.
          </p>
        </div>

        {/* Google Business Profile Card */}
        <div className="mb-12 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-2xl p-6 sm:p-8">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
                Grand Marabout Africain<span className="seo-hidden"> Marseille</span>
              </h3>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <span className="text-yellow-500 font-semibold">4.9</span>
                <span className="text-gray-400 text-sm">(847 avis)</span>
              </div>
              <p className="text-gray-400 mb-4 text-sm sm:text-base">
                Grand marabout africain et voyant médium. Spécialiste du <span className="text-green-500">retour affectif rapide</span> en 7 jours. 30 ans d'expérience, 15,000 couples réunis.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <MapPin className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                  <span>Consultations disponibles<span className="seo-hidden"> Marseille 13001</span></span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                  <span>Ouvert 7j/7 · 8h - 23h30</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Phone className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                  <a href="tel:+33745449641">07 45 44 96 41</a>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 justify-center md:justify-end">
              <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
                <Shield className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-500">99% Réussite</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
                <Award className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-yellow-500">30 ans d'exp.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="border-t border-gray-800 pt-8">
          <div className="text-center space-y-4">
            <div>
              <p className="text-white font-semibold mb-2">Grand Marabout Africain - Voyant Médium<span className="seo-hidden"> Marseille Bouches-du-Rhône</span></p>
              <a href="tel:+33745449641" className="text-green-500 font-semibold inline-flex items-center gap-2">
                <Phone className="h-4 w-4" />
                +33 7 45 44 96 41
              </a>
              <p className="text-gray-500 text-sm mt-2">Consultations 7j/7 de 8h à 23h30</p>
            </div>

            <div className="pt-6 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © {currentYear} Maître Diamma - Voyant Médium. Tous droits réservés.
              </p>
            </div>

            <div>
              <a 
                href="https://wa.me/33745449641" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-500"
              >
                Contacter sur WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
